import { useEffect, useRef } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { useCreateCheckout } from '../api.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { PreviewPlaceholder } from '../components/PreviewPlaceholder.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useStoryblokPreview } from '../contexts/StoryblokPreviewContext.js';

/**
 * Direct subscribe page. Accepts a `priceId` query parameter and immediately
 * initiates a Stripe Checkout session, redirecting the user to Stripe.
 *
 * Usage: /subscribe?priceId=<price_id>
 *
 * Send this URL to a prospective subscriber. On arrival the page fires the
 * checkout mutation automatically — no extra click required.
 */
export const SubscribePage = () => {
  const [searchParams] = useSearchParams();
  const priceId = searchParams.get('priceId');
  const createCheckout = useCreateCheckout();
  const attempted = useRef(false);
  const { isAuthenticated, isLoading: authIsLoading } = useAuth();
  const { isPreviewMode, isLoading: previewIsLoading } = useStoryblokPreview();
  const location = useLocation();

  useEffect(() => {
    if (!priceId || attempted.current || !isAuthenticated || isPreviewMode) {
      return;
    }

    attempted.current = true;

    createCheckout.mutate(
      { priceId },
      {
        onSuccess: ({ url }) => {
          window.location.href = url;
        },
      },
    );
  }, [priceId, isAuthenticated, isPreviewMode]);

  if (!authIsLoading && !previewIsLoading && !isAuthenticated && !isPreviewMode) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  if (isPreviewMode) {
    return <PreviewPlaceholder />;
  }

  if (!priceId) {
    return (
      <div className="mx-auto max-w-prose py-8">
        <Card>
          <div className="text-center">
            <h1 className="text-xl font-medium text-text">Invalid Link</h1>
            <p className="mt-2 text-sm text-text-secondary">
              This subscribe link is missing a price. Please contact support.
            </p>
            <Button href="/" className="mt-6">
              Go Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (createCheckout.isError) {
    return (
      <div className="mx-auto max-w-prose py-8">
        <Card>
          <div className="text-center">
            <h1 className="text-xl font-medium text-text">Something Went Wrong</h1>
            <p className="mt-2 text-sm text-text-secondary">
              We couldn't start your checkout session. Please try again.
            </p>
            <Button
              onClick={() => {
                attempted.current = false;
                createCheckout.mutate(
                  { priceId },
                  {
                    onSuccess: ({ url }) => {
                      window.location.href = url;
                    },
                  },
                );
              }}
              className="mt-6"
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-prose py-8">
      <Card>
        <div className="flex flex-col items-center gap-3 text-center">
          <Spinner size="sm" />
          <p className="text-sm text-text-secondary">Preparing your checkout&hellip;</p>
        </div>
      </Card>
    </div>
  );
};
